import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { parseDuration, formatDuration } from "@/lib/durationParse";

/**
 * Click-to-edit duration cell.
 *
 * Click → text input pre-filled with current value (e.g. "1h 30m").
 * Type "90", "1h 30m", "2h", "45m" — Enter commits, Esc cancels.
 *
 * The parent's onCommit receives the new minutes integer; it's responsible
 * for updating the task duration AND adjusting the schedule block's end_at
 * = start + duration (so the block grows/shrinks in place rather than the
 * start_at jumping).
 */
export default function DurationEditor({ value, onCommit, disabled, dataTestid }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const display = formatDuration(value);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const parsed = parseDuration(text);
    if (parsed != null && parsed > 0 && parsed !== value) {
      onCommit(parsed);
    }
    setEditing(false);
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {editing && !disabled ? (
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          className="mono text-xs tabular-nums px-1 w-14 bg-bg-elevated text-amber border border-amber/60 outline-none"
          data-testid={dataTestid ? `${dataTestid}-input` : undefined}
          placeholder="90 / 1h30m"
          spellCheck={false}
          autoComplete="off"
        />
      ) : (
        <button
          className={clsx(
            "mono text-xs tabular-nums px-1 truncate",
            disabled ? "text-ink-mute cursor-not-allowed" : "text-ink hover:text-amber"
          )}
          onClick={() => {
            if (disabled) return;
            setText(display);
            setEditing(true);
          }}
          disabled={disabled}
          data-testid={dataTestid}
          title="Type a duration — 90, 1h 30m, 2h, 45m all work"
        >
          {display}
        </button>
      )}
    </div>
  );
}
